import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { LoginArea } from '@/components/auth/LoginArea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { NOSTR_101_URL } from '@/lib/constants';
import { ExternalLink, Shield, Key, Globe, Vote } from 'lucide-react';

const features = [
  {
    icon: Key,
    text: 'Own your identity with cryptographic keys',
  },
  {
    icon: Shield,
    text: 'No censorship, no deplatforming, ever',
  },
  {
    icon: Globe,
    text: 'Open protocol - works across hundreds of apps',
  },
];

export function NostrCTA() {
  const { user } = useCurrentUser();

  return (
    <section className="relative py-16 sm:py-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card className="relative overflow-hidden border-primary/20 bg-gradient-to-br from-primary/[0.08] via-card to-card">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/[0.04] rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary/[0.03] rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

          <CardContent className="relative p-8 sm:p-10 lg:p-12">
            <div className="space-y-6 text-center">
              <div className="space-y-3">
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground">
                  New to <span className="text-bitcoin-gradient">Nostr</span>?
                </h2>
                <p className="text-muted-foreground text-sm sm:text-base max-w-lg mx-auto leading-relaxed">
                  Nostr is a decentralized social protocol built on the same principles as Bitcoin.
                  No company controls it. No one can censor you. Your votes are signed with your keys and belong to you forever.
                </p>
              </div>

              {/* Features */}
              <div className="grid gap-3 max-w-md mx-auto">
                {features.map((feature) => {
                  const Icon = feature.icon;
                  return (
                    <div key={feature.text} className="flex items-center gap-3 text-left">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-sm text-foreground/80">{feature.text}</span>
                    </div>
                  );
                })}
              </div>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
                {!user ? (
                  <LoginArea className="w-full sm:w-auto" />
                ) : (
                  <Button
                    asChild
                    className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-6"
                  >
                    <Link to="/vote">
                      <Vote className="w-4 h-4 mr-2" />
                      Go Vote Now
                    </Link>
                  </Button>
                )}
                <a
                  href={NOSTR_101_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-foreground/70 hover:text-primary transition-colors px-4 py-2 rounded-md border border-border/50 hover:border-primary/30"
                >
                  Learn More About Nostr
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
